import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { EditableTimeSegment, LeaveCategory, Shift, DisplayGroup } from '../../shared/types.js';
import { LockIcon } from './icons';

interface LeaveEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { leaveCategoryId: string, hoursPerDay: number }) => void;
    segment: EditableTimeSegment | null;
    leaveCategories: LeaveCategory[];
    getGroupForEmployee: (employeeId: string) => DisplayGroup | undefined;
    shifts: Shift[];
    isLocked?: boolean;
}

const toDateInputString = (timestamp: number): string => {
    const date = new Date(timestamp);
    const tzoffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzoffset).toISOString().split('T')[0];
};

const LeaveEditorModal: React.FC<LeaveEditorModalProps> = ({ isOpen, onClose, onSave, segment, leaveCategories, getGroupForEmployee, shifts, isLocked = false }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [leaveCategoryId, setLeaveCategoryId] = useState('');
    const [hours, setHours] = useState(8);
    const [date, setDate] = useState('');
    const [shiftId, setShiftId] = useState<string | null>(null);
    const customLeaveCategories = useMemo(() => leaveCategories.filter(c => c.source === 'kiosk'), [leaveCategories]);
    useEffect(() => {
        if (isOpen && segment) {
            const category = customLeaveCategories.find(c => c.id === segment.activityId);
            setLeaveCategoryId(category?.id || '');
            setHours(segment.hours);
            setDate(toDateInputString(segment.startTime));
            
            const group = getGroupForEmployee(segment.employeeId);
            setShiftId(group?.settings.shiftId || null);
        }
    }, [isOpen, segment]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);
    if (!segment) return null;
    if (isLocked) {
        return (
             <Modal isOpen={isOpen} onClose={onClose} title="View Leave Entry">
                <div className="text-center p-4">
                    <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold">Entry Locked</h3>
                    <p className="text-gray-500 mt-2">This leave entry is in a closed payroll period and cannot be edited.</p>
                     <div className="mt-6">
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                    </div>
                </div>
            </Modal>
        );
    }
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({
            leaveCategoryId,
            hoursPerDay: hours,
        });
        onClose();
    };
    const inputClasses = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    const readonlyInputClasses = `${inputClasses} bg-gray-100 dark:bg-gray-900/50 cursor-default`;
    const renderInputRow = (label: string, children: React.ReactNode) => (
        <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-right text-gray-600 dark:text-gray-300 font-medium">{label}:</label>
            <div className="col-span-2">{children}</div>
        </div>
    );
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Edit Leave Entry">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 text-gray-900 dark:text-white">
                {renderInputRow("Name", <input type="text" value={segment.employeeName} disabled className={`${inputClasses} disabled:opacity-70 disabled:cursor-not-allowed`} />)}
                {renderInputRow("Leave Type", (
                    <select value={leaveCategoryId} onChange={e => setLeaveCategoryId(e.target.value)} required className={inputClasses}>
                        {customLeaveCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                ))}
                {renderInputRow("Date", (
                    <input type="date" value={date} disabled className={readonlyInputClasses} />
                ))}
                {renderInputRow("Hours Assigned", (
                    <input type="number" value={hours} onChange={e => setHours(parseFloat(e.target.value) || 0)} min="0" step="0.5" required className={inputClasses} />
                ))}
                <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </div>
            </form>
        </Modal>
    );
};

export default LeaveEditorModal;
